import { Actor, Canvas, Engine, Random, Vector } from 'excalibur';
import { approach, toRadians } from '../utils/angles';
import { SEEDS } from '../utils/constants';
import { Palette } from '../utils/palette';
import { css, sketchStroke, wobblyCirclePath, wobblyLinePath } from '../utils/sketch';

export interface SideToggleOptions {
  name: string;
  pos: Vector;
  radius: number;
}

export class SideToggle extends Actor {
  readonly hitRadius: number;
  private readonly radius: number;
  private time = 0;
  private pressAmount = 0;
  private pressed = false;

  constructor(options: SideToggleOptions) {
    super({
      name: options.name,
      pos: options.pos,
      graphic: new Canvas({
        width: options.radius * 2 + 36,
        height: options.radius * 2 + 36,
        cache: false,
        draw: (ctx) => this.paint(ctx),
      }),
    });
    this.radius = options.radius;
    this.hitRadius = options.radius + 12;
  }

  setPressed(pressed: boolean): void {
    this.pressed = pressed;
  }

  isPointInside(x: number, y: number): boolean {
    const dx = x - this.pos.x;
    const dy = y - this.pos.y;
    return dx * dx + dy * dy <= this.hitRadius * this.hitRadius;
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    this.pressAmount = approach(this.pressAmount, this.pressed ? 1 : 0, dt * 10);
    this.rotation = toRadians(Math.sin(this.time * 2.2) * 5 - this.pressAmount * 8);
    const scale = 1 + this.pressAmount * 0.12;
    this.scale = new Vector(scale, scale);
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const rng = new Random(SEEDS.sideToggle);
    const mid = this.radius + 18;
    ctx.save();
    ctx.translate(mid, mid);

    ctx.save();
    ctx.fillStyle = css(Palette.shadow, 0.14);
    wobblyCirclePath(ctx, 2, 3, this.radius, rng, 1.4, 26);
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = css(Palette.paper, 0.98);
    wobblyCirclePath(ctx, 0, 0, this.radius - 2, rng, 1.6, 30);
    ctx.fill();
    ctx.restore();

    sketchStroke(ctx, rng, { color: Palette.ink, width: 2.8, alpha: 0.9 }, (c) =>
      wobblyCirclePath(c, 0, 0, this.radius, rng, 1.6, 30), 2, 1.2);

    const arrow = (direction: number, offsetY: number): void => {
      const startX = -this.radius * 0.38 * direction;
      const endX = this.radius * 0.34 * direction;
      const style = { color: Palette.blueDeep, width: 3.4 };
      sketchStroke(ctx, rng, style, (c) => wobblyLinePath(c, startX, offsetY, endX, offsetY, rng, 1, 4), 1);
      sketchStroke(ctx, rng, style, (c) =>
        wobblyLinePath(c, endX, offsetY, endX - direction * this.radius * 0.26, offsetY - this.radius * 0.2, rng, 1, 3), 1);
      sketchStroke(ctx, rng, style, (c) =>
        wobblyLinePath(c, endX, offsetY, endX - direction * this.radius * 0.26, offsetY + this.radius * 0.2, rng, 1, 3), 1);
    };
    arrow(1, -this.radius * 0.22);
    arrow(-1, this.radius * 0.22);

    ctx.restore();
  }
}
